<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, vue/html-closing-bracket-newline, @intlify/vue-i18n/no-raw-text */
import { computed, inject, proxyRefs, ref } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['navigate']);
const realData = inject('imudeiRealData');
const { error, loading, refresh, saving } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'postSale'),
    screen: computed(() => 'create'),
  })
);

const searchQuery = ref('');

const asArray = value => (Array.isArray(value) ? value : []);
const valueAt = (record, path) =>
  path.split('.').reduce((value, key) => value?.[key], record);
const firstValue = (record, ...paths) => {
  const value = paths
    .map(path => valueAt(record, path))
    .find(item => item !== undefined && item !== null && item !== '');

  return value ?? '';
};
const money = (value, currency = 'BRL') => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'object') {
    return value.formatted || value.display || money(value.amount, currency);
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(numeric);
};
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const imageUrlFrom = value => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return imageUrlFrom(value[0]);

  return imageUrlFrom(
    firstValue(value, 'url', 'src', 'image_url', 'file_url', 'preview_url')
  );
};
const saleImage = transaction =>
  imageUrlFrom(
    firstValue(
      transaction,
      'cover_url',
      'cover_image_url',
      'image_url',
      'thumbnail_url',
      'property.cover_url',
      'property.cover_image_url',
      'property.image_url',
      'imovel.cover_url',
      'imovel.cover_image_url',
      'imovel.image_url',
      'midias',
      'media',
      'images'
    )
  );
const participantAvatar = participant =>
  imageUrlFrom(
    firstValue(
      participant,
      'avatar_url',
      'thumbnail_url',
      'contact.avatar_url',
      'contact.thumbnail_url'
    )
  );
const date = value => {
  if (!value) return 'Data não retornada';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
};
const addressFor = transaction => {
  const address = firstValue(
    transaction,
    'address.endereco',
    'address.logradouro',
    'address.full_address'
  );
  const number = firstValue(transaction, 'address.numero');
  const unit = firstValue(
    transaction,
    'address.unidade',
    'address.complemento'
  );

  return (
    [address, number, unit].filter(Boolean).join(' · ') ||
    'Endereço não retornado'
  );
};
const participantFor = transaction => {
  const participants = asArray(transaction?.participants);
  const buyer = participants.find(participant =>
    /comprador|buyer/i.test(
      `${participant?.role || ''} ${participant?.relation_type || ''}`
    )
  );

  return buyer || participants[0] || null;
};
const participantsFor = transaction =>
  asArray(transaction?.participants).slice(0, 2);
const saleTitle = transaction =>
  firstValue(transaction, 'title', 'address.title', 'reference') ||
  `Venda #${transaction?.id || '—'}`;
const saleCode = transaction =>
  firstValue(transaction, 'reference', 'id') || '—';
const saleAmount = transaction =>
  money(
    firstValue(
      transaction,
      'contracted_total',
      'financial.contracted_total',
      'total_amount'
    ),
    firstValue(transaction, 'currency', 'financial.currency') || 'BRL'
  );
const saleCompletionDate = transaction =>
  date(
    firstValue(
      transaction,
      'completed_at',
      'closed_at',
      'finished_at',
      'concluded_at',
      'updated_at'
    )
  );
const transactionRows = computed(() =>
  asArray(realData.data.value?.createOptions?.transactions)
);
const filteredTransactions = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase('pt-BR');
  if (!query) return transactionRows.value;

  return transactionRows.value.filter(transaction =>
    `${saleTitle(transaction)} ${saleCode(transaction)} ${addressFor(
      transaction
    )} ${participantFor(transaction)?.name || ''}`
      .toLocaleLowerCase('pt-BR')
      .includes(query)
  );
});
const unavailableSalesMessage = computed(() => {
  if (loading.value) return 'Carregando vendas reais…';
  if (error.value) return error.value;
  if (searchQuery.value.trim()) {
    return 'Nenhuma venda real corresponde à busca.';
  }

  return 'A API não retornou vendas disponíveis para seleção.';
});
const visibleTransactions = computed(() =>
  filteredTransactions.value.slice(0, 5)
);
const selectedTransaction = computed(() =>
  transactionRows.value.find(
    transaction =>
      String(transaction.id) === String(model.form.property_transaction_id)
  )
);
const optionalFields = computed(() =>
  model.fields.filter(field => field.key !== 'property_transaction_id')
);
const configuredFields = computed(
  () =>
    optionalFields.value.filter(field =>
      String(model.form[field.key] || '').trim()
    ).length
);
const isSelected = transaction =>
  String(transaction?.id) === String(model.form.property_transaction_id);
const chooseTransaction = transaction => {
  if (!transaction?.id) return;

  model.form.property_transaction_id = String(transaction.id);
};
const submit = async () => {
  if (!model.canCreate || !model.form.property_transaction_id) return;

  const response = await model.create();
  if (!response) return;

  const id = response.id || response.post_sale_case_id;
  if (!id) {
    model.localError =
      'A API concluiu a solicitação, mas não retornou a identificação do processo.';
    return;
  }

  emit('navigate', { id: String(id), screen: 'detail' });
};
</script>

<template>
  <main class="mx-auto w-full max-w-[1320px] px-4 pb-20 pt-4 text-[#101958]">
    <header
      data-imudei-visual-id="post-sale-create-header"
      class="flex items-start justify-between gap-4"
    >
      <div>
        <h1 class="text-[22px] font-bold tracking-[-0.035em]">
          Novo processo de pós-venda
        </h1>
        <p class="mt-1 text-[9px] text-[#69759c]">
          Crie seu processo em etapas e acompanhe cada marco com dados reais.
        </p>
      </div>
      <button
        class="flex h-10 items-center gap-2 rounded-lg border border-[#ded5fb] px-4 text-[8px] font-semibold text-[#4e20c1]"
        disabled
        title="A API não retornou conteúdo de ajuda para este fluxo"
        type="button"
      >
        <i class="i-lucide-circle-play size-4" />Como funciona
      </button>
    </header>

    <section
      data-imudei-visual-id="post-sale-create-steps"
      class="mt-4 grid grid-cols-5 gap-3"
    >
      <span class="flex min-w-0 items-center gap-2">
        <i
          class="grid size-9 shrink-0 place-items-center rounded-full border-2 border-violet-300 bg-white text-[10px] font-bold text-[#5a24c7] ring-4 ring-violet-100"
          >1</i
        >
        <span class="min-w-0">
          <strong class="block text-[9px] text-[#382070]">Venda</strong>
          <small class="block truncate text-[7px] text-[#69759c]">
            Selecione a venda
          </small>
        </span>
        <i class="ml-auto h-px min-w-5 bg-[#d8dce9]" />
      </span>
      <span class="flex min-w-0 items-center gap-2">
        <i
          class="grid size-9 shrink-0 place-items-center rounded-full border border-[#cad1e1] bg-white text-[10px] font-bold text-[#303858]"
          >2</i
        >
        <span class="min-w-0">
          <strong class="block text-[9px] text-[#303858]">Responsáveis</strong>
          <small class="block truncate text-[7px] text-[#69759c]">
            Defina quem participa
          </small>
        </span>
        <i class="i-lucide-lock-keyhole ml-auto size-3 text-[#9ba5bd]" />
        <i class="h-px min-w-4 bg-[#d8dce9]" />
      </span>
      <span class="flex min-w-0 items-center gap-2">
        <i
          class="grid size-9 shrink-0 place-items-center rounded-full border border-[#cad1e1] bg-white text-[10px] font-bold text-[#303858]"
          >3</i
        >
        <span class="min-w-0">
          <strong class="block text-[9px] text-[#303858]">Etapas</strong>
          <small class="block truncate text-[7px] text-[#69759c]">
            Estruture o processo
          </small>
        </span>
        <i class="i-lucide-lock-keyhole ml-auto size-3 text-[#9ba5bd]" />
        <i class="h-px min-w-4 bg-[#d8dce9]" />
      </span>
      <span class="flex min-w-0 items-center gap-2">
        <i
          class="grid size-9 shrink-0 place-items-center rounded-full border border-[#cad1e1] bg-white text-[10px] font-bold text-[#303858]"
          >4</i
        >
        <span class="min-w-0">
          <strong class="block text-[9px] text-[#303858]">Prazos</strong>
          <small class="block truncate text-[7px] text-[#69759c]">
            Defina prazos e SLAs
          </small>
        </span>
        <i class="i-lucide-lock-keyhole ml-auto size-3 text-[#9ba5bd]" />
        <i class="h-px min-w-4 bg-[#d8dce9]" />
      </span>
      <span class="flex min-w-0 items-center gap-2">
        <i
          class="grid size-9 shrink-0 place-items-center rounded-full border border-[#cad1e1] bg-white text-[10px] font-bold text-[#303858]"
          >5</i
        >
        <span class="min-w-0">
          <strong class="block text-[9px] text-[#303858]">Revisão</strong>
          <small class="block truncate text-[7px] text-[#69759c]">
            Revise e confirme
          </small>
        </span>
        <i class="i-lucide-lock-keyhole ml-auto size-3 text-[#9ba5bd]" />
      </span>
    </section>

    <section class="mt-5 grid grid-cols-[minmax(0,1fr)_214px] gap-4">
      <div class="min-w-0">
        <section
          data-imudei-visual-id="post-sale-create-progress"
          class="rounded-2xl border border-[#e8e4ef] bg-white px-4 py-4"
        >
          <header>
            <h2 class="text-[10px] font-bold">Etapas do processo</h2>
            <p class="mt-1 text-[8px] text-[#69759c]">
              Sua jornada, do início à conclusão.
            </p>
          </header>
          <div
            class="mt-3 grid grid-cols-[1fr_24px_1fr_24px_1fr_24px_1fr_24px_1fr_24px_1fr] items-center gap-1 text-center"
          >
            <span>
              <i
                class="mx-auto grid size-14 place-items-center overflow-hidden rounded-full border-2 border-violet-400 bg-violet-50 text-[#5a24c7] ring-4 ring-violet-100"
              >
                <img
                  v-if="selectedTransaction && saleImage(selectedTransaction)"
                  :alt="saleTitle(selectedTransaction)"
                  :src="saleImage(selectedTransaction)"
                  class="size-full object-cover"
                />
                <i v-else class="i-lucide-building-2 size-6" />
              </i>
              <strong class="mt-2 block text-[8px]">Venda</strong>
            </span>
            <i class="i-lucide-arrow-right size-4 text-[#c8cfdf]" />
            <span>
              <i
                class="mx-auto grid size-14 place-items-center rounded-full border-2 border-[#ffd0c8] bg-[#fff3ef] text-[#f07b62] ring-4 ring-[#fff8f5]"
              >
                <i class="i-lucide-users size-6" />
              </i>
              <strong class="mt-2 block text-[8px]">Responsáveis</strong>
            </span>
            <i class="i-lucide-arrow-right size-4 text-[#c8cfdf]" />
            <span>
              <i
                class="mx-auto grid size-14 place-items-center rounded-full border-2 border-[#cdd7fa] bg-[#f7f9ff] text-[#5a24c7] ring-4 ring-[#fafbff]"
              >
                <i class="i-lucide-list-tree size-6" />
              </i>
              <strong class="mt-2 block text-[8px]">Etapas</strong>
            </span>
            <i class="i-lucide-arrow-right size-4 text-[#c8cfdf]" />
            <span>
              <i
                class="mx-auto grid size-14 place-items-center rounded-full border-2 border-[#cdd7fa] bg-[#f7f9ff] text-[#223168] ring-4 ring-[#fafbff]"
              >
                <i class="i-lucide-calendar-days size-6" />
              </i>
              <strong class="mt-2 block text-[8px]">Prazos</strong>
            </span>
            <i class="i-lucide-arrow-right size-4 text-[#c8cfdf]" />
            <span>
              <i
                class="mx-auto grid size-14 place-items-center rounded-full border-2 border-[#cdd7fa] bg-[#f7f9ff] text-[#223168] ring-4 ring-[#fafbff]"
              >
                <i class="i-lucide-shield-check size-6" />
              </i>
              <strong class="mt-2 block text-[8px]">Revisão</strong>
            </span>
            <i class="i-lucide-arrow-right size-4 text-[#c8cfdf]" />
            <span>
              <i
                class="mx-auto grid size-14 place-items-center rounded-full border-2 border-violet-200 bg-violet-50 text-[#6a2ad0] ring-4 ring-violet-50"
              >
                <i class="i-lucide-gem size-6" />
              </i>
              <strong class="mt-2 block text-[8px]">Concluído</strong>
              <small class="mt-1 block text-[7px] text-[#7480a0]">
                Recompensa indisponível
              </small>
            </span>
          </div>
        </section>

        <form
          class="mt-3 grid grid-cols-[minmax(0,1fr)_300px] gap-3"
          @submit.prevent="submit"
        >
          <section
            data-imudei-visual-id="post-sale-create-sales"
            class="min-w-0 rounded-2xl border border-[#e8e4ef] bg-white p-4"
          >
            <header>
              <h2 class="text-[11px] font-bold">Selecione a venda concluída</h2>
              <p class="mt-1 text-[8px] text-[#7480a0]">
                Escolha uma venda retornada pelo backend para iniciar o
                processo.
              </p>
            </header>

            <div class="mt-3 flex items-center justify-between gap-3">
              <label
                class="flex h-8 w-[330px] max-w-full items-center gap-2 rounded-lg border border-[#e3deeb] px-3 text-[8px] text-[#8b95ae]"
              >
                <i class="i-lucide-search size-3.5" />
                <input
                  v-model="searchQuery"
                  class="w-full bg-transparent outline-none placeholder:text-[#8b95ae]"
                  placeholder="Localizar venda concluída"
                  type="search"
                />
              </label>
              <button
                class="flex h-8 shrink-0 items-center gap-2 rounded-lg border border-[#e3deeb] px-3 text-[8px] font-semibold text-[#3e286f]"
                type="button"
                @click="refresh"
              >
                <i class="i-lucide-refresh-cw size-3" />Atualizar
              </button>
            </div>

            <p
              v-if="visibleTransactions.length"
              class="mt-3 px-2 text-[7px] text-[#7480a0]"
            >
              {{ transactionRows.length }} venda{{
                transactionRows.length === 1 ? '' : 's'
              }}
              retornada{{ transactionRows.length === 1 ? '' : 's' }} pela API
            </p>
            <article
              v-if="!visibleTransactions.length"
              class="mt-3 flex min-h-[152px] flex-col items-center justify-center rounded-xl border border-dashed border-[#dcd6e8] bg-[#fcfbfd] px-6 text-center"
            >
              <i class="i-lucide-building-2 size-6 text-[#9aa4bb]" />
              <strong class="mt-3 text-[9px]">Nenhuma venda disponível</strong>
              <p class="mt-1 max-w-[280px] text-[8px] leading-4 text-[#7480a0]">
                {{ unavailableSalesMessage }}
              </p>
              <button
                class="mt-3 text-[8px] font-semibold text-[#5a24c7]"
                type="button"
                @click="refresh"
              >
                Tentar novamente
              </button>
            </article>

            <button
              v-for="transaction in visibleTransactions"
              :key="transaction.id"
              class="mt-2 grid min-h-[86px] w-full grid-cols-[minmax(0,1.62fr)_minmax(0,1.05fr)_minmax(0,.78fr)_20px] items-center gap-3 rounded-xl border px-2.5 py-2 text-left transition-colors"
              :class="
                isSelected(transaction)
                  ? 'border-[#7f45ff] bg-violet-50/50'
                  : 'border-[#efebf5] bg-white hover:bg-[#fcfaff]'
              "
              type="button"
              @click="chooseTransaction(transaction)"
            >
              <span class="flex min-w-0 items-center gap-2.5">
                <span
                  class="grid h-[68px] w-[130px] shrink-0 place-items-center overflow-hidden rounded-lg bg-gradient-to-br from-[#405571] to-[#dbc0ab] text-white"
                >
                  <img
                    v-if="saleImage(transaction)"
                    :alt="saleTitle(transaction)"
                    :src="saleImage(transaction)"
                    class="size-full object-cover"
                  />
                  <i v-else class="i-lucide-building-2 size-5" />
                </span>
                <span class="min-w-0">
                  <strong class="block truncate text-[8px]">
                    {{ saleTitle(transaction) }}
                  </strong>
                  <small class="mt-1 block truncate text-[7px] text-[#637198]">
                    {{ addressFor(transaction) }}
                  </small>
                  <small
                    class="mt-2 inline-block rounded bg-violet-50 px-1.5 py-0.5 text-[7px] font-semibold text-[#5a24c7]"
                  >
                    {{ saleCode(transaction) }}
                  </small>
                </span>
              </span>
              <span class="min-w-0">
                <small class="block text-[7px] text-[#657397]"
                  >Compradores</small
                >
                <span
                  v-if="participantsFor(transaction).length"
                  class="mt-2 flex items-center gap-1.5"
                >
                  <span class="flex -space-x-1.5">
                    <i
                      v-for="participant in participantsFor(transaction)"
                      :key="participant.id || participant.name"
                      class="grid size-6 place-items-center overflow-hidden rounded-full border border-white bg-pink-100 text-[6px] font-bold text-pink-600"
                    >
                      <img
                        v-if="participantAvatar(participant)"
                        :alt="participant.name || 'Participante'"
                        :src="participantAvatar(participant)"
                        class="size-full object-cover"
                      />
                      <template v-else>{{
                        initials(participant.name)
                      }}</template>
                    </i>
                  </span>
                  <span class="min-w-0 truncate text-[7px] font-semibold">
                    {{
                      participantFor(transaction)?.name ||
                      'Cliente não retornado'
                    }}
                  </span>
                </span>
                <small v-else class="mt-2 block text-[7px] text-[#8490ad]">
                  Cliente não retornado
                </small>
              </span>
              <span class="min-w-0">
                <small class="block text-[7px] text-[#657397]"
                  >Valor da venda</small
                >
                <strong class="mt-1 block truncate text-[9px]">
                  {{ saleAmount(transaction) }}
                </strong>
                <small class="mt-2 block text-[7px] text-[#637198]">
                  Concluída em {{ saleCompletionDate(transaction) }}
                </small>
              </span>
              <i
                class="i-lucide-circle-check-big size-5"
                :class="
                  isSelected(transaction) ? 'text-[#5b20ca]' : 'text-[#d4d9e7]'
                "
              />
            </button>

            <section
              v-if="optionalFields.length"
              class="mt-3 rounded-xl border border-[#eeeaf5] bg-[#fdfcff] p-3"
            >
              <header class="flex items-center justify-between gap-3">
                <span>
                  <strong class="block text-[8px]">Dados adicionais</strong>
                  <small class="mt-1 block text-[7px] text-[#7480a0]">
                    Campos opcionais aceitos pelo contrato de criação.
                  </small>
                </span>
                <span
                  class="rounded-full bg-violet-50 px-2.5 py-1 text-[7px] font-semibold text-[#5a24c7]"
                >
                  {{ configuredFields }}/{{ optionalFields.length }}
                </span>
              </header>
              <div class="mt-3 grid grid-cols-2 gap-2">
                <label
                  v-for="field in optionalFields"
                  :key="field.key"
                  class="min-w-0"
                >
                  <span
                    class="mb-1 block truncate text-[7px] font-semibold text-[#657097]"
                  >
                    {{ field.label }}
                  </span>
                  <select
                    v-if="field.type === 'select'"
                    v-model="model.form[field.key]"
                    class="h-8 w-full rounded-lg border border-[#e4deec] bg-white px-2 text-[8px] outline-none focus:border-[#a88bea]"
                  >
                    <option value="">Não informado</option>
                    <option
                      v-for="option in field.options"
                      :key="option"
                      :value="option"
                    >
                      {{ option }}
                    </option>
                  </select>
                  <input
                    v-else
                    v-model="model.form[field.key]"
                    class="h-8 w-full rounded-lg border border-[#e4deec] bg-white px-2 text-[8px] outline-none placeholder:text-[#a0a8ba] focus:border-[#a88bea]"
                    :placeholder="`${field.label} não informado`"
                    :type="field.type"
                  />
                </label>
              </div>
              <p class="mt-2 text-[7px] text-[#8a94ad]">
                A API não retornou catálogo de usuários; informe IDs válidos da
                conta apenas quando necessário.
              </p>
            </section>
          </section>

          <aside
            data-imudei-visual-id="post-sale-create-summary"
            class="space-y-3"
          >
            <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
              <h2 class="text-[10px] font-bold">Resumo do processo</h2>
              <div class="mt-4 flex items-center gap-3">
                <span
                  class="grid size-12 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-[#435b70] to-[#e9c5ab] text-white"
                >
                  <img
                    v-if="selectedTransaction && saleImage(selectedTransaction)"
                    :alt="saleTitle(selectedTransaction)"
                    :src="saleImage(selectedTransaction)"
                    class="size-full object-cover"
                  />
                  <i v-else class="i-lucide-building-2 size-5" />
                </span>
                <span class="min-w-0">
                  <strong class="block truncate text-[9px]">
                    {{
                      selectedTransaction
                        ? saleTitle(selectedTransaction)
                        : 'Venda não selecionada'
                    }}
                  </strong>
                  <small class="mt-1 block truncate text-[7px] text-[#7480a0]">
                    {{
                      selectedTransaction
                        ? addressFor(selectedTransaction)
                        : 'Aguardando uma venda real'
                    }}
                  </small>
                </span>
              </div>
              <p class="mt-4 flex justify-between gap-3 text-[8px]">
                <span class="text-[#7480a0]">Venda selecionada</span>
                <b class="max-w-[55%] truncate text-right">
                  {{
                    selectedTransaction ? saleCode(selectedTransaction) : '—'
                  }}
                </b>
              </p>
              <p class="mt-4 flex justify-between gap-3 text-[8px]">
                <span class="text-[#7480a0]">Modelo de processo</span>
                <b class="max-w-[55%] text-right">Após criação</b>
              </p>
              <p class="mt-4 flex justify-between gap-3 text-[8px]">
                <span class="text-[#7480a0]">Etapas incluídas</span>
                <b class="max-w-[55%] text-right">A definir</b>
              </p>
              <p class="mt-4 flex justify-between gap-3 text-[8px]">
                <span class="text-[#7480a0]">Previsão de conclusão</span>
                <b class="max-w-[55%] text-right">Indisponível</b>
              </p>
            </article>

            <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
              <header class="flex items-center gap-2">
                <span
                  class="grid size-7 place-items-center rounded-full bg-violet-50 text-[#5a24c7]"
                >
                  <i class="i-lucide-star size-3.5" />
                </span>
                <span>
                  <h2 class="text-[9px] font-bold">Clube de recompensas</h2>
                  <p class="mt-0.5 text-[7px] text-[#7480a0]">
                    Ações responsáveis do processo.
                  </p>
                </span>
              </header>
              <p class="mt-4 text-[8px] leading-4 text-[#69759c]">
                Pontos, missões e regras de recompensa não são retornados pelo
                contrato atual de pós-venda.
              </p>
              <div
                class="mt-4 space-y-2 border-t border-[#f0edf5] pt-3 text-[7px]"
              >
                <p class="flex justify-between gap-3">
                  <span>Missões</span><b class="text-[#8490ad]">Indisponível</b>
                </p>
                <p class="flex justify-between gap-3">
                  <span>Pontos</span><b class="text-[#8490ad]">Indisponível</b>
                </p>
                <p class="flex justify-between gap-3">
                  <span>Ranking</span><b class="text-[#8490ad]">Indisponível</b>
                </p>
              </div>
            </article>
          </aside>
        </form>
      </div>

      <aside data-imudei-visual-id="post-sale-create-social" class="space-y-3">
        <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <h2 class="text-[10px] font-bold">Missão diária</h2>
          <p class="mt-1 text-[7px] text-[#637198]">Dados sociais da conta</p>
          <div class="mt-4 flex items-center gap-3">
            <span
              class="grid size-12 place-items-center rounded-full border-[5px] border-violet-100 text-[#8b95ae]"
            >
              <i class="i-lucide-circle-slash-2 size-4" />
            </span>
            <span>
              <strong class="block text-[8px]">Indisponível</strong>
              <small class="mt-1 block text-[7px] leading-3 text-[#7480a0]">
                A API não retornou uma missão ativa.
              </small>
            </span>
          </div>
        </article>
        <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <h2 class="text-[10px] font-bold">Reconhecimento</h2>
          <p class="mt-1 text-[7px] text-[#637198]">Pessoas que colaboram</p>
          <div
            class="mt-4 rounded-xl border border-dashed border-[#e0dbea] bg-[#fcfbfd] px-3 py-5 text-center"
          >
            <i
              class="i-lucide-users-round mx-auto block size-5 text-[#9aa4bb]"
            />
            <p class="mt-2 text-[7px] leading-3 text-[#7480a0]">
              A API não retornou ranking ou reconhecimento.
            </p>
          </div>
          <span
            class="mt-3 block rounded-lg border border-[#e5e0ed] px-3 py-2 text-center text-[7px] text-[#8b95ae]"
          >
            Ranking indisponível
          </span>
        </article>
        <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <h2 class="text-[10px] font-bold">Conquistas</h2>
          <p class="mt-1 text-[7px] text-[#637198]">Suas conquistas recentes</p>
          <div class="mt-4 space-y-2">
            <p
              v-for="label in [
                'Primeiro processo',
                'Documentos em dia',
                'Etapas no ritmo',
              ]"
              :key="label"
              class="flex items-center gap-2 rounded-xl border border-[#eeeaf5] p-2"
            >
              <i
                class="grid size-7 place-items-center rounded-lg bg-[#f4f1f8] text-[#9aa4bb]"
              >
                <i class="i-lucide-lock-keyhole size-3" />
              </i>
              <span class="min-w-0">
                <strong class="block text-[7px]">{{ label }}</strong>
                <small class="mt-0.5 block text-[6px] text-[#8490ad]">
                  Não retornada pela API
                </small>
              </span>
            </p>
          </div>
          <span
            class="mt-3 block rounded-lg border border-[#e5e0ed] px-3 py-2 text-center text-[7px] text-[#8b95ae]"
          >
            Conquistas indisponíveis
          </span>
        </article>
      </aside>
    </section>

    <p
      v-if="model.unavailableMessage"
      class="fixed bottom-[76px] right-7 z-50 max-w-sm rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 text-[9px] leading-4 text-[#6540a8]"
    >
      {{ model.unavailableMessage }}
    </p>
    <p
      v-else-if="model.localError || error"
      class="fixed bottom-[76px] right-7 z-50 max-w-sm rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[9px] leading-4 text-rose-600"
    >
      {{ model.localError || error }}
    </p>
    <p
      v-else-if="model.notice"
      class="fixed bottom-[76px] right-7 z-50 max-w-sm rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-[9px] leading-4 text-emerald-700"
    >
      {{ model.notice }}
    </p>
  </main>

  <footer
    data-imudei-visual-id="post-sale-create-footer"
    class="fixed bottom-0 left-[192px] right-0 z-40 flex h-[64px] items-center justify-end gap-3 border-t border-[#e9e4ef] bg-white/95 px-6 shadow-[0_-8px_24px_rgba(49,25,100,0.05)] backdrop-blur"
  >
    <button
      class="mr-auto rounded-lg border border-[#cdbdff] px-8 py-2.5 text-[9px] font-semibold text-[#4d1ab7]"
      type="button"
      @click="emit('navigate', 'dashboard')"
    >
      Cancelar
    </button>
    <span
      class="rounded-lg border border-[#cdbdff] px-6 py-2.5 text-[9px] text-[#8b95ae]"
      title="O backend não oferece rascunho para pós-venda"
    >
      Rascunho indisponível
    </span>
    <button
      class="flex min-w-[166px] items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#4d1ab7] to-[#c82ecf] px-7 py-2.5 text-[9px] font-bold text-white disabled:cursor-not-allowed disabled:bg-[#e0e4ef] disabled:bg-none disabled:text-[#7e87a1] disabled:opacity-100"
      :disabled="
        saving || !model.canCreate || !model.form.property_transaction_id
      "
      type="button"
      @click="submit"
    >
      {{ saving ? 'Criando…' : 'Continuar' }}
      <i v-if="!saving" class="i-lucide-arrow-right size-4" />
    </button>
  </footer>
</template>
